import {
  type IAIReceptionistCustomQA,
  type IAIReceptionistCustomSection,
  type IAIReceptionistKnowledgeBase,
  type IImportExistingContent,
  type IImportExistingCustomSection,
  type IImportExistingQaPair,
  type IImportProposal,
  type IImportProposedCustomSection,
  type IImportProposedQaPair,
} from '@principle-theorem/principle-core/interfaces';
import { compact, isNil, omit } from 'lodash';
import { ImportProposal } from './import-proposal';

/**
 * Owns how a proposed Q&A pair or custom section is matched to the existing
 * item it would update: by its reference, by its question or title, and by
 * whether it changes that item's text. The import, the review and the merge
 * share these rules, so they agree on which item an update changes.
 */
export class ImportProposalMatching {
  /**
   * Makes each proposed Q&A pair or custom section that does not reference an
   * existing item of the Knowledge Base into an update of the existing pair
   * asking the same question, or the existing section with the same title. A
   * reference naming an item no longer in the Knowledge Base is dropped first.
   * The extractor is asked to set these references but does not always, and
   * accepting such an item as new would leave two answers to one question.
   *
   * Two kinds of item are left out, so every item left is either a change to
   * one existing item or asks something the Knowledge Base does not answer:
   * an update repeating the existing answer or content, which the request's
   * shortened or blank existing content lets through, and an item asking
   * what another proposed item already updates. Repeats are left out before
   * anything is linked, so a repeat never holds the link a real change needs.
   * The review runs it against the Knowledge Base being edited, so every
   * reference it shows is one `ImportProposalAcceptance.merge` updates.
   */
  static linkMatchingExisting(
    proposal: IImportProposal,
    knowledgeBase: IAIReceptionistKnowledgeBase
  ): IImportProposal {
    return {
      ...proposal,
      sectionQaPairs: ImportProposal.mapSectionQaPairs(
        proposal.sectionQaPairs,
        (pairs, key) =>
          ImportProposalMatching._linkMatchingQaPairs(
            pairs,
            knowledgeBase.sectionQaPairs?.[key] ?? []
          )
      ),
      customSections: ImportProposalMatching._linkMatchingCustomSections(
        proposal.customSections,
        knowledgeBase.customSections ?? []
      ),
    };
  }

  /**
   * Leaves out updates that would change nothing: a Q&A pair whose answer
   * is the answer of the existing pair it references, and a custom section
   * whose content is the content of the existing section it references, as
   * sent in the request and compared with `isSameText`. The extractor is
   * told an update needs a new fact or a correction, yet a repeat import of
   * the same pages still proposes the existing text, and in the review those
   * rows bury the ones that change something. Run it after
   * `ImportExistingContent.validateExistingRefs`, so every reference it
   * follows names existing content that was sent.
   */
  static dropUnchangedUpdates(
    proposal: IImportProposal,
    existing: IImportExistingContent
  ): IImportProposal {
    return {
      ...proposal,
      sectionQaPairs: ImportProposal.mapSectionQaPairs(
        proposal.sectionQaPairs,
        (pairs, key) =>
          pairs.filter(
            (pair) =>
              !ImportProposalMatching._isUnchangedQaPair(
                pair,
                existing.sectionQaPairs[key] ?? []
              )
          )
      ),
      customSections: proposal.customSections.filter(
        (section) =>
          !ImportProposalMatching._isUnchangedCustomSection(
            section,
            existing.customSections
          )
      ),
    };
  }

  /**
   * Whether two texts say the same, ignoring case and how the words are
   * spaced or broken across lines. The import and its review both use it to
   * recognise a proposed value that only repeats what the practice already
   * has, so they agree on what counts as unchanged.
   */
  static isSameText(first: string, second: string): boolean {
    return (
      ImportProposalMatching._normaliseForComparison(first) ===
      ImportProposalMatching._normaliseForComparison(second)
    );
  }

  /**
   * Finds the existing pair asking the same question, ignoring case and
   * surrounding spaces.
   */
  static findMatchingQaPair(
    existing: IAIReceptionistCustomQA[] | undefined,
    question: string
  ): IAIReceptionistCustomQA | undefined {
    const normalisedQuestion =
      ImportProposalMatching._normaliseForMatching(question);
    return existing?.find(
      (pair) =>
        ImportProposalMatching._normaliseForMatching(pair.question) ===
        normalisedQuestion
    );
  }

  /**
   * Finds the existing custom section with the same title, ignoring case and
   * surrounding spaces.
   */
  static findMatchingCustomSection(
    existing: IAIReceptionistCustomSection[] | undefined,
    title: string
  ): IAIReceptionistCustomSection | undefined {
    const normalisedTitle = ImportProposalMatching._normaliseForMatching(title);
    return existing?.find(
      (section) =>
        ImportProposalMatching._normaliseForMatching(section.title) ===
        normalisedTitle
    );
  }

  /**
   * Decides, for each item's reference in order, whether the item keeps it:
   * only when `isTarget` accepts the reference and no earlier item holds the
   * same one, so one existing item is never updated by two proposed items.
   */
  static keepsRef<Ref>(
    refs: (Ref | undefined)[],
    isTarget: (ref: Ref) => boolean
  ): boolean[] {
    return refs.map(
      (ref, index) =>
        !isNil(ref) && isTarget(ref) && refs.indexOf(ref) === index
    );
  }

  static isIndexInRange(index: number, length: number): boolean {
    return Number.isInteger(index) && index >= 0 && index < length;
  }

  private static _linkMatchingQaPairs(
    pairs: IImportProposedQaPair[],
    existingPairs: IAIReceptionistCustomQA[]
  ): IImportProposedQaPair[] {
    const changedPairs = pairs.filter(
      (pair) => !ImportProposalMatching._isUnchangedQaPair(pair, existingPairs)
    );
    const existingIds = new Set(existingPairs.map((pair) => pair.id));
    const keepsRef = ImportProposalMatching.keepsRef(
      changedPairs.map((pair) => pair.existingId),
      (existingId) => existingIds.has(existingId)
    );
    const linkedIds = new Set(
      compact(
        changedPairs.map((pair, index) => keepsRef[index] && pair.existingId)
      )
    );
    return changedPairs.flatMap((pair, index) => {
      if (keepsRef[index]) {
        return [pair];
      }
      const unlinked = omit(pair, 'existingId');
      const match = ImportProposalMatching.findMatchingQaPair(
        existingPairs,
        pair.question
      );
      if (!match) {
        return [unlinked];
      }
      if (
        linkedIds.has(match.id) ||
        ImportProposalMatching.isSameText(match.answer, pair.answer)
      ) {
        return [];
      }
      linkedIds.add(match.id);
      return [{ ...unlinked, existingId: match.id }];
    });
  }

  private static _linkMatchingCustomSections(
    sections: IImportProposedCustomSection[],
    existingSections: IAIReceptionistCustomSection[]
  ): IImportProposedCustomSection[] {
    const changedSections = sections.filter(
      (section) =>
        !ImportProposalMatching._isUnchangedCustomSection(
          section,
          existingSections
        )
    );
    const keepsRef = ImportProposalMatching.keepsRef(
      changedSections.map((section) => section.existingIndex),
      (existingIndex) =>
        ImportProposalMatching.isIndexInRange(
          existingIndex,
          existingSections.length
        )
    );
    const linkedIndexes = new Set(
      changedSections.flatMap((section, index) =>
        keepsRef[index] && !isNil(section.existingIndex)
          ? [section.existingIndex]
          : []
      )
    );
    return changedSections.flatMap((section, index) => {
      if (keepsRef[index]) {
        return [section];
      }
      const unlinked = omit(section, 'existingIndex');
      const normalisedTitle = ImportProposalMatching._normaliseForMatching(
        section.title
      );
      const matchIndex = existingSections.findIndex(
        (existing) =>
          ImportProposalMatching._normaliseForMatching(existing.title) ===
          normalisedTitle
      );
      if (matchIndex < 0) {
        return [unlinked];
      }
      if (
        linkedIndexes.has(matchIndex) ||
        ImportProposalMatching.isSameText(
          existingSections[matchIndex].content,
          section.content
        )
      ) {
        return [];
      }
      linkedIndexes.add(matchIndex);
      return [{ ...unlinked, existingIndex: matchIndex }];
    });
  }

  private static _normaliseForMatching(text: string): string {
    return text.trim().toLowerCase();
  }

  private static _normaliseForComparison(text: string): string {
    return text.replace(/\s+/g, ' ').trim().toLowerCase();
  }

  private static _isUnchangedQaPair(
    pair: IImportProposedQaPair,
    existingPairs: IImportExistingQaPair[]
  ): boolean {
    const updatedPair = existingPairs.find(
      (existingPair) => existingPair.id === pair.existingId
    );
    return (
      !!updatedPair &&
      ImportProposalMatching.isSameText(updatedPair.answer, pair.answer)
    );
  }

  private static _isUnchangedCustomSection(
    section: IImportProposedCustomSection,
    existingSections: IImportExistingCustomSection[]
  ): boolean {
    if (isNil(section.existingIndex)) {
      return false;
    }
    const updatedSection: IImportExistingCustomSection | undefined =
      existingSections[section.existingIndex];
    return (
      !!updatedSection &&
      ImportProposalMatching.isSameText(updatedSection.content, section.content)
    );
  }
}
